import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import "./Form.css";
import { requiredFieldClass } from "../lib/requiredFieldClass";
import { applicationSuccessMessage } from "../lib/applicationSuccessMessage";

const DRAFT_KEY = "draft_paid_leave";
const CALENDAR_DATES_KEY = "calendar_paid_leave_dates";

/** 申請対象日数（スケジュール上の最小申請単位） */
const formatYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** 今日の日付文字列（YYYY-MM-DD） */
function getToday() {
  return formatYmd(new Date());
}

/** 今日からN日後の日付文字列（YYYY-MM-DD） */
function getDaysLater(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return formatYmd(d);
}

const WEEK_LABELS = ["日", "月", "火", "水", "木", "金", "土"];

const PaidLeaveForm = ({ onBack, onNext, isFirstForm, isLastForm, isMultiForm, id, isDraft }) => {
  // 「私用のため」を含む新しい選択肢
  const REASON_OPTIONS = ['病欠', '私用のため', '欠勤', 'その他'];

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    selectedDates: [],
    reasonType: "",
    reasonOther: "",
  });
  const [showCalendar, setShowCalendar] = useState(false);
  // カレンダー表示中の月（1日固定）
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });

  /**
   * 申請可能な最小日付：
   * - 通常：2週間後以降
   * - 病欠：今日以降（当日・翌日も申請可）
   */
  const minDate = useMemo(() => {
    if (formData.reasonType === "病欠") return getToday();
    return getDaysLater(14);
  }, [formData.reasonType]);

  const toggleDate = (date) => {
    setFormData((prev) => {
      const dates = prev.selectedDates.includes(date)
        ? prev.selectedDates.filter((d) => d !== date)
        : [...prev.selectedDates, date];
      return { ...prev, selectedDates: dates.sort() };
    });
  };

  useEffect(() => {
    // カレンダーから渡された有給日付を読み込む
    try {
      const calendarDates = localStorage.getItem(CALENDAR_DATES_KEY);
      if (calendarDates) {
        const parsed = JSON.parse(calendarDates);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setFormData((prev) => ({ ...prev, selectedDates: parsed.sort() }));
          localStorage.removeItem(CALENDAR_DATES_KEY);
          return;
        }
        localStorage.removeItem(CALENDAR_DATES_KEY);
      }
    } catch (_) {}

    // 一時保存データを読み込む
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setFormData((prev) => ({
          ...prev,
          ...parsed,
          reasonOther: (parsed.reasonOther ?? '').toString().slice(0, 30),
        }));
      }
    } catch (_) {}
  }, []);

  // 病欠以外に戻した場合、最小日付より前の選択日は除外する
  useEffect(() => {
    setFormData((prev) => {
      if (prev.selectedDates.length === 0) return prev;
      const kept = prev.selectedDates.filter((d) => d >= minDate);
      if (kept.length === prev.selectedDates.length) return prev;
      return { ...prev, selectedDates: kept };
    });
  }, [minDate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === 'reasonOther') {
        return { ...prev, reasonOther: value.slice(0, 30) };
      }
      if (name === 'reasonType') {
        return { ...prev, reasonType: value, reasonOther: value === 'その他' ? prev.reasonOther : '' };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleSaveDraft = async() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
    alert("一時保存しました。");
  };

  const handleDeleteDraft = async () => {
    const confirmDelete = window.confirm("この下書き（一時保存データ）を削除してもよろしいですか？\n※削除すると元に戻せません。");
    if (!confirmDelete) return;

    setLoading(true);
    try {
      if (id) {
      await api.delete(`/api/applications/drafts/${id}/`);
      }
      
      // ローカルでも削除
      localStorage.removeItem(DRAFT_KEY);
      
      alert('下書きを削除しました。');
      navigate('/applications');
    } catch (error) {
      console.error('下書きの削除に失敗:', error);
      alert('下書きの削除に失敗しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    } 
  };

  const handleBack = () => {
    if (onBack) onBack();
    else navigate("/applications");
  };

  const goAfterSubmit = async () => {
    try {
      const ci = await api.get("/company-info/");
      if (ci.data?.use_employee_schedule) {
        navigate("../schedule");
        return;
      }
    } catch (_) {}
    if (isMultiForm && onNext && !isLastForm) {
      onNext();
      return;
    }
    navigate("/applications");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.selectedDates.length === 0) {
      alert("有給日を選択してください。");
      return;
    }
    if (!formData.reasonType) {
      alert("理由を選択してください。");
      return;
    }
    if (formData.reasonType === 'その他') {
      const other = (formData.reasonOther || '').trim();
      if (!other) {
        alert('「その他」を選択した場合は理由を入力してください。');
        return;
      }
      if (other.length > 30) {
        alert('理由は30文字以内で入力してください。');
        return;
      }
    }

    if (!window.confirm("有給休暇申請を送信してもよろしいですか？")) return;

    const effectiveReason = formData.reasonType === 'その他'
      ? formData.reasonOther.trim()
      : formData.reasonType;

    try {
      await api.post("/forms/submit_paid_leave/", {
        selectedDates: formData.selectedDates,
        reason: effectiveReason,
      });
      localStorage.removeItem(DRAFT_KEY);
      alert(
        applicationSuccessMessage("有給休暇申請が正常に提出されました。", [
          "申請一覧から内容・状態を確認できます。",
        ])
      );
      await goAfterSubmit();
    } catch (error) {
      if (error.response?.data) {
        alert(typeof error.response.data === "string" ? error.response.data : JSON.stringify(error.response.data));
      } else {
        alert("送信に失敗しました。もう一度お試しください。");
      }
    }
  };

  const canSubmit = useMemo(
    () => Boolean(
      formData.selectedDates.length > 0 &&
      formData.reasonType &&
      (formData.reasonType !== 'その他' || formData.reasonOther.trim())
    ),
    [formData.selectedDates, formData.reasonType, formData.reasonOther]
  );

  /* ===== カレンダー描画 ===== */
  const calendarYear = calendarMonth.getFullYear();
  const calendarMonthIndex = calendarMonth.getMonth();

  const calendarCells = useMemo(() => {
    const firstDay = new Date(calendarYear, calendarMonthIndex, 1);
    const startWeekday = firstDay.getDay();
    const daysInMonth = new Date(calendarYear, calendarMonthIndex + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i += 1) cells.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push(new Date(calendarYear, calendarMonthIndex, day));
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [calendarYear, calendarMonthIndex]);

  const goPrevMonth = () => {
    setCalendarMonth(new Date(calendarYear, calendarMonthIndex - 1, 1));
  };
  const goNextMonth = () => {
    setCalendarMonth(new Date(calendarYear, calendarMonthIndex + 1, 1));
  };

  const handlePickDate = (d) => {
    if (!d) return;
    const ymd = formatYmd(d);
    if (ymd < minDate) return;
    toggleDate(ymd);
  };

  // 表示中の月が最小日付の月より前なら戻れない
  const minMonthDate = useMemo(() => {
    const [y, m] = minDate.split("-").map(Number);
    return new Date(y, m - 1, 1);
  }, [minDate]);
  const canGoPrev =
    new Date(calendarYear, calendarMonthIndex, 1).getTime() > minMonthDate.getTime();

  return (
    <div className="container form-container">
      <h3>有給休暇申請</h3>
      <p className="form-note">
        {formData.reasonType === "病欠"
          ? "※ 病欠は当日・翌日も申請できます。"
          : `※ 2週間前までに申請してください（${minDate} 以降の日付のみ選択可）。`}
      </p>

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          {/* 休暇日ラベルをクリックすると、カレンダーが開閉する */}
          <button
            type="button"
            className="paid-leave-day-toggle"
            onClick={() => setShowCalendar((v) => !v)}
            aria-expanded={showCalendar}
          >
            <span className="form-label" style={{ marginBottom: 0 }}>休暇日</span>
            <span className="paid-leave-day-toggle-icon" aria-hidden="true">
              {showCalendar ? "▲" : "▼"}
            </span>
          </button>

          {showCalendar && (
            <div className="paid-leave-calendar">
              <div className="paid-leave-calendar-head">
                <button
                  type="button"
                  className="paid-leave-calendar-nav"
                  onClick={goPrevMonth}
                  disabled={!canGoPrev}
                  aria-label="前の月"
                >
                  ‹
                </button>
                <div className="paid-leave-calendar-title">
                  {calendarYear}年 {calendarMonthIndex + 1}月
                </div>
                <button
                  type="button"
                  className="paid-leave-calendar-nav"
                  onClick={goNextMonth}
                  aria-label="次の月"
                >
                  ›
                </button>
              </div>

              <div className="paid-leave-calendar-weekdays">
                {WEEK_LABELS.map((w, i) => (
                  <div
                    key={w}
                    className={`paid-leave-calendar-weekday ${i === 0 ? "is-sun" : i === 6 ? "is-sat" : ""}`}
                  >
                    {w}
                  </div>
                ))}
              </div>

              <div className="paid-leave-calendar-grid">
                {calendarCells.map((d, idx) => {
                  if (!d) {
                    return <div key={`empty-${idx}`} className="paid-leave-calendar-cell is-empty" />;
                  }
                  const ymd = formatYmd(d);
                  const isSelected = formData.selectedDates.includes(ymd);
                  const isDisabled = ymd < minDate;
                  const weekday = d.getDay();
                  return (
                    <button
                      key={ymd}
                      type="button"
                      className={[
                        "paid-leave-calendar-cell",
                        isSelected ? "is-selected" : "",
                        isDisabled ? "is-disabled" : "",
                        weekday === 0 ? "is-sun" : "",
                        weekday === 6 ? "is-sat" : "",
                      ].filter(Boolean).join(" ")}
                      onClick={() => handlePickDate(d)}
                      disabled={isDisabled}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>

              <p className="paid-leave-calendar-hint">
                日付をタップで選択、もう一度タップで解除します。
              </p>
            </div>
          )}

          <div className="selected-dates-list mt-2">
            {formData.selectedDates.map((date) => (
              <span key={date} className="paid-leave-date-chip">
                {date}
                <button
                  type="button"
                  className="paid-leave-date-remove"
                  onClick={() => toggleDate(date)}
                  aria-label={`${date} を解除`}
                >×</button>
              </span>
            ))}
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="reason" className="form-label">申請理由</label>
          <select
            id="reason"
            name="reasonType"
            className={requiredFieldClass(formData.reasonType)}
            value={formData.reasonType}
            onChange={handleInputChange}
            required
          >
            <option value="">-- 選択 --</option>
            {REASON_OPTIONS.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        {formData.reasonType === 'その他' && (
          <div className="mb-3">
            <label htmlFor="reasonOther" className="form-label">その他の理由（30文字まで）</label>
            <input
              type="text"
              id="reasonOther"
              name="reasonOther"
              className={requiredFieldClass(formData.reasonOther)}
              value={formData.reasonOther}
              onChange={handleInputChange}
              maxLength={30}
              required
            />
            <div className="text-muted" style={{ fontSize: '0.9em' }}>
              {formData.reasonOther.length}/30
            </div>
          </div>
        )}

        {formData.selectedDates.length > 0 && (
          <div className="mb-3">
            <span className="form-note">日数：{formData.selectedDates.length} 日</span>
          </div>
        )}

        <div className="button-group">
          <button type="submit" className="btn-submit" disabled={!canSubmit}>
            申請
          </button>
          <button type="button" className="btn-draft" onClick={handleSaveDraft}>
            一時保存
          </button>
          {((id && isDraft) || localStorage.getItem(DRAFT_KEY)) && <button type="button" className="btn-delete-draft" onClick={handleDeleteDraft} >
            下書削除
          </button>}
          <button type="button" className="btn-back" onClick={handleBack}>
            {onBack && !isFirstForm ? "前へ" : "戻る"}
          </button>
          {isMultiForm && onNext && (
            <button type="button" className="btn-nav" onClick={onNext}>
              {isLastForm ? "完了" : "次へ"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default PaidLeaveForm;
